import { getSupabaseClient } from "../supabase/supabase";
import { ATTACHMENTS_BUCKET } from "./attachments";

/**
 * Vedlegg fra en PRIVAT bøtte.
 *
 * BØTTA `attachments` ER PRIVAT. RLS-reglene på `storage.objects` har alltid
 * begrenset lesing til `authenticated`, men så lenge bøtta var `public = true`
 * fantes stien `/object/public/...` som gikk utenom reglene helt. Da var hver
 * eneste fil lesbar for hvem som helst med URL-en, for alltid.
 *
 * HVORFOR VI FORTSATT LAGRER «PUBLIC»-URL-EN I BASEN. En signert URL utløper,
 * så den kan ikke lagres. Det vi lagrer må være en STABIL PEKER, og
 * public-URL-en er nettopp det: den inneholder stien, den er entydig å
 * kjenne igjen, og formatet er allerede i bruk i notater og produktbilder.
 * Alternativet — å lagre bare stien — ville krevd en datamigrering og gitt to
 * formater å støtte i overgangen, uten å gjøre noe bedre.
 *
 * Les den lagrede verdien som «URL-formet sti». Den svarer ikke hvis du
 * åpner den direkte, og det er meningen.
 */

// Fanger HELE URL-en til `.../storage/v1/object/public/attachments/<sti>`,
// vertsnavnet inkludert.
//
// VERTSNAVNET MÅ MED. Med bare stien i mønsteret byttet `byttUtIHtml` ut
// halve URL-en, og resultatet ble `https://prosjektet.supabase.co` +
// `https://signert/...` limt sammen — altså et brutt bilde overalt. Fanget av
// prøven, ikke av lesing.
//
// Vertsnavnet er valgfritt så en lagret verdi som bare er stien også treffer.
// Stien kan inneholde skråstreker (mapper), så vi stopper først på
// anførselstegn, mellomrom eller parentes — der en HTML-attributt slutter.
const MONSTER = new RegExp(
  `(?:https?://[^"'\\s)>]*?)?/storage/v1/object/public/${ATTACHMENTS_BUCKET}/([^"'\\s)>]+)`,
  "g",
);

/** Er dette en lagret peker inn i vedleggsbøtta? */
export const erVedleggUrl = (url?: string | null): boolean => {
  if (!url) return false;
  MONSTER.lastIndex = 0;
  return MONSTER.test(url);
};

/** Stien inne i bøtta, eller null om URL-en peker et annet sted. */
export const stiFraUrl = (url?: string | null): string | null => {
  if (!url) return null;
  MONSTER.lastIndex = 0;
  const treff = MONSTER.exec(url);
  if (!treff) return null;
  // Stier lagres URL-kodet; Storage-API-et vil ha dem dekodet.
  try {
    return decodeURIComponent(treff[1]);
  } catch {
    return treff[1];
  }
};

/** Alle unike vedleggsstier i en HTML-blokk. Tom liste = ingenting å signere. */
export const stierIHtml = (html?: string | null): string[] => {
  if (!html) return [];
  const funnet = new Set<string>();
  MONSTER.lastIndex = 0;
  let treff: RegExpExecArray | null;
  while ((treff = MONSTER.exec(html)) !== null) {
    try {
      funnet.add(decodeURIComponent(treff[1]));
    } catch {
      funnet.add(treff[1]);
    }
  }
  return [...funnet];
};

/**
 * Bytter hver lagret peker ut med den signerte URL-en fra kartet.
 *
 * Stier som mangler i kartet står urørt. Det er med vilje: et bilde som ikke
 * lot seg signere skal bli et synlig brutt bilde, ikke en tom `src` som
 * stille later som ingenting mangler.
 */
export const byttUtIHtml = (
  html: string,
  kart: Map<string, string>,
): string => {
  MONSTER.lastIndex = 0;
  return html.replace(MONSTER, (hel, rasti: string) => {
    let sti = rasti;
    try {
      sti = decodeURIComponent(rasti);
    } catch {
      /* bruk rå sti */
    }
    return kart.get(sti) ?? hel;
  });
};

/** Hvor lenge en signert lenke lever. */
export const SIGNERT_LEVETID_SEKUNDER = 60 * 60;

/**
 * Signerer stiene i én bunt. `createSignedUrls` gjør det i ett kall, så en
 * notatside med ti bilder koster én forespørsel, ikke ti.
 */
export const signerStier = async (
  stier: string[],
  levetid = SIGNERT_LEVETID_SEKUNDER,
): Promise<Map<string, string>> => {
  const kart = new Map<string, string>();
  if (stier.length === 0) return kart;

  const { data, error } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .createSignedUrls(stier, levetid);

  if (error) {
    // Én feilet signering skal ikke ta ned notatet den står i. Bildene blir
    // brutt og synlig borte; teksten rundt leses fortsatt.
    console.error("signerStier", error);
    return kart;
  }

  for (const rad of data ?? []) {
    // `path` kommer tilbake slik vi sendte den inn. Rader med feil har
    // `signedUrl` tom — de hoppes over og blir stående som brutt bilde.
    if (rad.path && rad.signedUrl) kart.set(rad.path, rad.signedUrl);
  }
  return kart;
};
